import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  ENDPOINTS,
  ROUND_WINDOW_MS,
  type AssetEntry,
  type LatestResponse,
} from "./api";

type ConnState = "connecting" | "live" | "stale" | "offline";

type Ctx = {
  assets: Record<string, AssetEntry> | null;
  lastRound: number | null;
  /** Rolling per-asset price history for sparklines. */
  history: Record<string, number[]>;
  /** Unix ms when the latest round was signed (anchor for the signed-ago counter). */
  anchorMs: number | null;
  connState: ConnState;
};

const PricesContext = createContext<Ctx | null>(null);
const HISTORY_LEN = 32;

/**
 * One SSE connection (with polling fallback) shared across the whole page.
 * Wraps the data flow from the previous index.html into a single React hook
 * so any component can subscribe without re-opening EventSource.
 */
export function PricesProvider({ children }: { children: ReactNode }) {
  const [assets, setAssets] = useState<Record<string, AssetEntry> | null>(null);
  const [lastRound, setLastRound] = useState<number | null>(null);
  const [anchorMs, setAnchorMs] = useState<number | null>(null);
  const [history, setHistory] = useState<Record<string, number[]>>({});
  const [connState, setConnState] = useState<ConnState>("connecting");
  const lastRoundRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    let es: EventSource | null = null;
    let pollId: number | null = null;
    let bootedFromSse = false;

    const apply = (next: LatestResponse["assets"]) => {
      if (cancelled || !next) return;
      setAssets(next);
      const btc = next["BTC/USD"];
      const primaryRound = btc?.round_id ?? null;
      if (primaryRound != null && primaryRound !== lastRoundRef.current) {
        lastRoundRef.current = primaryRound;
        setLastRound(primaryRound);
        setAnchorMs(primaryRound * ROUND_WINDOW_MS);
        setConnState("live");
      }
      setHistory((h) => {
        const updated = { ...h };
        for (const [key, entry] of Object.entries(next)) {
          const prev = updated[key] ?? [];
          const v = entry.price_human;
          if (typeof v !== "number") continue;
          const nextSeries = [...prev, v];
          updated[key] =
            nextSeries.length > HISTORY_LEN
              ? nextSeries.slice(-HISTORY_LEN)
              : nextSeries;
        }
        return updated;
      });
    };

    const poll = async () => {
      try {
        const r = await fetch(ENDPOINTS.latest, { cache: "no-store" });
        const j = (await r.json()) as LatestResponse;
        if (j?.assets) apply(j.assets);
      } catch {
        setConnState("offline");
      }
    };

    try {
      es = new EventSource(ENDPOINTS.stream);
      es.addEventListener("prices", (e) => {
        try {
          const parsed = JSON.parse((e as MessageEvent).data) as LatestResponse;
          if (parsed?.assets) {
            bootedFromSse = true;
            apply(parsed.assets);
          }
        } catch {
          /* swallow malformed messages */
        }
      });
      es.onerror = () => {
        if (!bootedFromSse) setConnState("connecting");
      };
    } catch {
      /* EventSource unavailable — fall straight to polling */
    }

    // If SSE doesn't deliver within 4s, fall back to polling.
    const fallbackTimer = window.setTimeout(() => {
      if (!bootedFromSse) {
        es?.close();
        es = null;
        poll();
        pollId = window.setInterval(poll, 2000);
      }
    }, 4000);

    // Staleness watcher: if the latest round is > 10s old, flip to STALE.
    const staleTimer = window.setInterval(() => {
      if (lastRoundRef.current == null) return;
      const ageS = (Date.now() - lastRoundRef.current * ROUND_WINDOW_MS) / 1000;
      setConnState((s) =>
        s === "offline" ? "offline" : ageS > 10 ? "stale" : "live"
      );
    }, 1000);

    return () => {
      cancelled = true;
      clearTimeout(fallbackTimer);
      clearInterval(staleTimer);
      es?.close();
      if (pollId != null) clearInterval(pollId);
    };
  }, []);

  return (
    <PricesContext.Provider
      value={{ assets, lastRound, anchorMs, history, connState }}
    >
      {children}
    </PricesContext.Provider>
  );
}

export function usePrices(): Ctx {
  const ctx = useContext(PricesContext);
  if (!ctx) {
    throw new Error("usePrices must be used inside <PricesProvider>");
  }
  return ctx;
}

/** Hook: the live "signed Xs ago" string, ticking at ~10 Hz via rAF. */
export function useSignedAgo(): string {
  const { anchorMs } = usePrices();
  const [text, setText] = useState("—");

  useEffect(() => {
    if (anchorMs == null) {
      setText("—");
      return;
    }
    let raf = 0;
    let last = 0;
    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      if (now - last < 80) return;
      last = now;
      const sec = Math.max(0, (Date.now() - anchorMs) / 1000);
      setText(sec.toFixed(1) + "s");
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [anchorMs]);

  return text;
}
